import { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';

// Post-migration advisory bar shown across the top of every page. Persistent
// (not a transient toast) but dismissible per-browser. Bump DISMISS_KEY to
// re-show it to everyone after a later round of changes; clearing it here has
// no effect on users who already dismissed the previous key.
const DISMISS_KEY = 'br-site-notice-2026-07';

const SiteNotice = () => {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Private-mode / storage-blocked: just hide for this session.
    }
    setDismissed(true);
  };

  return (
    <Box
      role="status"
      sx={{
        backgroundColor: '#fef3c7', // amber-100
        color: '#78350f', // amber-900
        borderBottom: '1px solid #fcd34d', // amber-300
        // The header logo is a ~100px circle pinned to the top-left of the
        // page (position: absolute) on desktop, overlapping this bar. Pad
        // the left so the text clears it. On mobile (<=640px) the logo goes
        // static/centered, so only the normal padding is needed there.
        pl: { xs: 2, sm: '120px' },
        pr: { xs: 2, md: 3 },
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{ flex: 1, lineHeight: 1.5, fontSize: { xs: '0.8rem', md: '0.875rem' } }}
      >
        We&rsquo;ve recently rolled out a major update to the site. A few things
        may not work quite right yet &mdash; if you run into any problems, please{' '}
        <RouterLink
          to="/contact"
          style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}
        >
          contact us
        </RouterLink>{' '}
        and we&rsquo;ll get it resolved as soon as possible.
      </Typography>
      <IconButton
        aria-label="Dismiss notice"
        size="small"
        onClick={dismiss}
        sx={{ color: 'inherit', flexShrink: 0 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default SiteNotice;
